import { reactive, ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type {
  IncidentReportDetailsDto,
  AddressDto,
  BountyDto,
  Geometry,
  PreviewIncidentReportResponse,
  RecordedCrashDto
} from '@roadfootage/api-client'
import { AddressPrecision, CrashDateTimePrecision } from '@roadfootage/api-client'
import type { IncidentReportType } from '@roadfootage/api-client'

import { useFileUploadStore } from '../file-upload'
import { useApi } from '../../composables/api'
import { regionConfig } from '@/config/region'
import { getExtractor } from '@/utils/vendors/registry'
import type { ExtractedIncidentMeta } from '@/utils/vendors/types'
import { reverseGeocodeToAddressDto } from '@/composables/useNominatim'
import { getTimezoneFromCoordinates } from '@/utils/timezone'
import logger from '../../logger'

export type StepName =
  | 'start'
  | 'vendor-browse'
  | 'media-upload'
  | 'incident-details'
  | 'refine-location'
  | 'recorded-crash'
  | 'review-submit'

/** Where an auto-filled incident time came from: the file's date, or GPS/time the dashcam recorded. */
export type DateTimeSource = 'file' | 'dashcam'

export interface ExtractedMetadataHints {
  dateTime: false | DateTimeSource
  location: boolean
  description: boolean
}

export interface MetadataWarning {
  dateTime: boolean
  location: boolean
}

export interface LocalIncidentReportState extends IncidentReportDetailsDto {
  deviceVendor: string | null
  localLatitude: number | null
  localLongitude: number | null
  extractedMetadataHints: ExtractedMetadataHints
  metadataWarning: MetadataWarning
}

const initReportDetailsForm = (): LocalIncidentReportState => ({
  incidentReportType: undefined,
  primaryUserIncidentFileId: null,
  countryCode: regionConfig.countryCode,
  location: null,
  incidentDateTime: null,
  incidentTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  userSubmittedIncidentDescription: null,
  address: {
    rawInput: null,
    formattedAddress: null,
    precision: AddressPrecision.Approximate
  } as AddressDto,
  deviceVendor: null,
  localLatitude: null,
  localLongitude: null,
  extractedMetadataHints: { dateTime: false, location: false, description: false },
  metadataWarning: { dateTime: false, location: false },

  // --- The following are static on submit. ---

  // Moderators will determine if the incident requires a content warning based on the media and details provided by the user.
  hasContentWarning: false,

  // The user must confirm the disclaimer before proceeding with the report submission.
  isFilmedInPublicLocation: true,
  userConsentProvided: true
})

// Recorded crash lookup (RF-071): up to 5 crashes within 500 m and 24 h of the pin, and never worth more than 5 s of waiting.
const CRASH_LOOKUP_RADIUS_METERS = 500
const CRASH_LOOKUP_WINDOW_HOURS = 24
const CRASH_LOOKUP_TIMEOUT_MS = 5000
const MAX_CRASH_CHOICES = 5

export const useIncidentSubmissionStore = defineStore('incidentSubmission', () => {
  const { creatorIncidentReportsApi } = useApi()
  // Silent: a failed lookup skips the step instead of showing a toast.
  const { crashesApi } = useApi({ notifyOnError: false })

  const step = ref<StepName>('start')
  const maxStepIndex = ref<number>(0)
  const isSubmitting = ref<boolean>(false)
  const nearbyCrashes = ref<RecordedCrashDto[]>([])
  const selectedCrashId = ref<string | null>(null)
  // The crash the wizard was started from (recorded crash page). It stays on offer even if the lookup misses it.
  const startedFromCrash = ref<RecordedCrashDto | null>(null)
  const isLoadingCrashes = ref<boolean>(false)
  // The approved report this footage shows the same incident as (RF-133). Sent on create as a pending relation.
  const relatedTo = ref<{ id: string } | null>(null)

  const incidentForm: LocalIncidentReportState = reactive(initReportDetailsForm())

  const activeStepOrder = computed<StepName[]>(() => {
    const steps: StepName[] = ['start']
    if (getExtractor(incidentForm.deviceVendor).supportsDirectoryBrowse) {
      steps.push('vendor-browse')
    }
    steps.push('media-upload', 'incident-details', 'refine-location')
    if (nearbyCrashes.value.length > 0) {
      steps.push('recorded-crash')
    }
    steps.push('review-submit')
    return steps
  })

  const currentStepIndex = computed(() => activeStepOrder.value.indexOf(step.value))
  const isLastStep = computed(() => step.value === 'review-submit')

  function updateGeoJsonLocation() {
    if (incidentForm.localLongitude !== null && incidentForm.localLatitude !== null) {
      incidentForm.location = {
        type: 'Point',
        coordinates: [incidentForm.localLongitude, incidentForm.localLatitude]
      } as Geometry & { coordinates: number[] }
    } else {
      incidentForm.location = null
    }
  }

  function $reset() {
    step.value = 'start'
    maxStepIndex.value = 0
    isSubmitting.value = false
    nearbyCrashes.value = []
    selectedCrashId.value = null
    startedFromCrash.value = null
    isLoadingCrashes.value = false
    relatedTo.value = null
    Object.assign(incidentForm, initReportDetailsForm())
  }

  // Start the wizard from what a bounty asks for. Real file metadata may overwrite the time and location later.
  function prefillFromBounty(bounty: BountyDto) {
    const coordinates = bounty.location?.coordinates
    if (coordinates && coordinates.length >= 2) {
      incidentForm.localLongitude = coordinates[0]
      incidentForm.localLatitude = coordinates[1]
      updateGeoJsonLocation()
    }
    if (bounty.address) incidentForm.address = { ...incidentForm.address, ...bounty.address }
    if (bounty.windowStartDateTime) incidentForm.incidentDateTime = bounty.windowStartDateTime
    if (bounty.windowTimezone) incidentForm.incidentTimezone = bounty.windowTimezone
    if (bounty.incidentReportType) incidentForm.incidentReportType = bounty.incidentReportType
  }

  // Start the wizard from another person's report of the same incident. Real file metadata may overwrite the time and location later.
  function prefillFromRelatedReport(report: PreviewIncidentReportResponse) {
    const coordinates = report.location?.coordinates
    if (coordinates && coordinates.length >= 2) {
      incidentForm.localLongitude = coordinates[0]
      incidentForm.localLatitude = coordinates[1]
      updateGeoJsonLocation()
    }
    if (report.address) {
      // The required address text falls back to the formatted address when the original has no typed input.
      incidentForm.address = {
        ...incidentForm.address,
        ...report.address,
        rawInput: report.address.rawInput || report.address.formattedAddress || null
      }
    }
    if (report.incidentDateTime) incidentForm.incidentDateTime = report.incidentDateTime
    if (report.incidentTimezone) incidentForm.incidentTimezone = report.incidentTimezone
    if (report.incidentReportType) {
      incidentForm.incidentReportType = report.incidentReportType as IncidentReportType
    }
    relatedTo.value = report.id ? { id: report.id } : null
  }

  function clearRelated() {
    relatedTo.value = null
  }

  // Start the wizard from a recorded crash. The time is filled only when the record has an exact one: Month and Hour
  // precision crashes have no real time, so the user types when it happened.
  function prefillFromCrash(crash: RecordedCrashDto) {
    startedFromCrash.value = crash
    selectedCrashId.value = crash.id ?? null
    if (crash.latitude !== undefined && crash.longitude !== undefined) {
      incidentForm.localLatitude = crash.latitude
      incidentForm.localLongitude = crash.longitude
      updateGeoJsonLocation()
    }
    if (crash.address) incidentForm.address = { ...incidentForm.address, ...crash.address }
    if (crash.crashTimezone) incidentForm.incidentTimezone = crash.crashTimezone
    if (crash.dateTimePrecision === CrashDateTimePrecision.Exact && crash.crashDateTime) {
      incidentForm.incidentDateTime = crash.crashDateTime
    }
  }

  function selectCrash(id: string | null) {
    selectedCrashId.value = id
  }

  function pinIsFarFrom(crash: RecordedCrashDto): boolean {
    const { localLatitude: lat, localLongitude: lng } = incidentForm
    if (lat === null || lng === null) return false
    if (crash.latitude === undefined || crash.longitude === undefined) return false
    const rad = Math.PI / 180
    const dLat = (crash.latitude - lat) * rad
    const dLng = (crash.longitude - lng) * rad
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat * rad) * Math.cos(crash.latitude * rad) * Math.sin(dLng / 2) ** 2
    const metres = 2 * 6371000 * Math.asin(Math.sqrt(a))
    return metres > CRASH_LOOKUP_RADIUS_METERS
  }

  // The crash the user came from is always on offer, first, so a missed or failed lookup cannot lose the link.
  function keepStartedFromCrash() {
    const started = startedFromCrash.value
    if (!started) return
    // Once the pin has moved out of the lookup radius this is no longer that crash: forget it.
    if (pinIsFarFrom(started)) {
      startedFromCrash.value = null
      return
    }
    const others = nearbyCrashes.value.filter((c) => c.id !== started.id)
    nearbyCrashes.value = [started, ...others].slice(0, MAX_CRASH_CHOICES)
  }

  // Looks up recorded crashes near the pin. Never throws: an empty list, a failure or a timeout all mean "skip the step".
  async function loadNearbyCrashes(): Promise<void> {
    nearbyCrashes.value = []
    const { localLatitude, localLongitude, incidentDateTime } = incidentForm
    if (localLatitude === null || localLongitude === null || !incidentDateTime) {
      keepStartedFromCrash()
      if (!startedFromCrash.value) selectedCrashId.value = null
      return
    }

    isLoadingCrashes.value = true
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), CRASH_LOOKUP_TIMEOUT_MS)
    try {
      const found = await crashesApi.crashesGetNearby(
        {
          lat: localLatitude,
          lng: localLongitude,
          at: new Date(incidentDateTime),
          radiusMeters: CRASH_LOOKUP_RADIUS_METERS,
          windowHours: CRASH_LOOKUP_WINDOW_HOURS
        },
        { signal: controller.signal }
      )
      nearbyCrashes.value = found.slice(0, MAX_CRASH_CHOICES)
    } catch (error) {
      console.error('Store: Recorded crash lookup failed, skipping the step', error)
    } finally {
      clearTimeout(timeout)
      isLoadingCrashes.value = false
    }

    keepStartedFromCrash()
    // A choice only survives when that crash is still on offer.
    if (!nearbyCrashes.value.some((c) => c.id === selectedCrashId.value)) {
      selectedCrashId.value = null
    }
  }

  // Fill time and place from what the dashcam recorded (GPS in the footage, or a Tesla event). Browser-only: the
  // files are never sent anywhere to read this. Results that arrive after the Upload step are dropped, so they can
  // never overwrite what the user has typed since.
  function applyDashcamMetadata(meta: ExtractedIncidentMeta) {
    const order = activeStepOrder.value
    if (currentStepIndex.value > order.indexOf('media-upload')) return

    if (meta.dateTime) {
      incidentForm.incidentDateTime = meta.dateTime
      incidentForm.extractedMetadataHints.dateTime = 'dashcam'
    }
    if (meta.lat === undefined || meta.lon === undefined) return

    const { lat, lon } = meta
    incidentForm.localLatitude = lat
    incidentForm.localLongitude = lon
    updateGeoJsonLocation()
    const tz = getTimezoneFromCoordinates(lat, lon)
    if (tz) incidentForm.incidentTimezone = tz
    incidentForm.extractedMetadataHints.location = true
    void fillAddress(lat, lon)
  }

  async function fillAddress(lat: number, lon: number) {
    // The coordinates as text keep the required address field valid if the lookup fails.
    const fallback: AddressDto = {
      rawInput: `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
      formattedAddress: null,
      precision: AddressPrecision.Approximate
    }
    incidentForm.address = fallback
    let address: AddressDto
    try {
      address = await reverseGeocodeToAddressDto(lat, lon)
    } catch (e) {
      // Never log the coordinates.
      logger.warn(e, 'Reverse geocode of dashcam location failed')
      return
    }
    // The user (or a newer result) changed the place or address while we waited.
    const unchanged =
      incidentForm.localLatitude === lat &&
      incidentForm.localLongitude === lon &&
      incidentForm.address?.rawInput === fallback.rawInput
    // A 200 without address text would blank the required field, so keep the coordinates then.
    if (unchanged && address.rawInput) incidentForm.address = address
  }

  function nextStep() {
    const order = activeStepOrder.value
    const nextIdx = currentStepIndex.value + 1
    if (nextIdx < order.length) {
      step.value = order[nextIdx]
      if (nextIdx > maxStepIndex.value) {
        maxStepIndex.value = nextIdx
      }
    }
  }

  // Continue from Refine: the lookup decides whether the crash step exists. If the user jumped elsewhere meanwhile, stay put.
  async function continueFromRefine(): Promise<void> {
    await loadNearbyCrashes()
    if (step.value !== 'refine-location') return
    nextStep()
  }

  function previousStep() {
    const order = activeStepOrder.value
    const prevIdx = currentStepIndex.value - 1
    if (prevIdx >= 0) {
      step.value = order[prevIdx]
    }
  }

  function jumpToStep(targetStep: StepName) {
    const order = activeStepOrder.value
    const targetIdx = order.indexOf(targetStep)
    if (targetIdx !== -1 && targetIdx <= maxStepIndex.value) {
      step.value = targetStep
    }
  }

  function isStepDone(stepName: StepName): boolean {
    return activeStepOrder.value.indexOf(stepName) < maxStepIndex.value
  }

  // Resolves to the created report's id, or null when the submit failed.
  async function submitReport(): Promise<string | null> {
    if (isSubmitting.value) return null
    isSubmitting.value = true

    // 1. Data Preparation (Outside the try/catch)
    const fileUploadStore = useFileUploadStore()
    const validFiles = fileUploadStore.userFileList.filter(
      (f) => f.isUploaded && !f.hasError && f.id
    )
    const validFileIds = validFiles.map((f) => f.id as string)

    const primary = validFiles.find((f) => f.isPrimary)
    if (primary && primary.id) {
      incidentForm.primaryUserIncidentFileId = primary.id
    }

    const payload = {
      incidentReportDetails: incidentForm,
      openDataCrashId: selectedCrashId.value,
      relatedIncidentReportId: relatedTo.value?.id ?? null,
      userFileIds: validFileIds
    }

    // 2. Focused Exception Handling (Only the API call)
    try {
      const created = await creatorIncidentReportsApi.creatorIncidentReportsSubmitReport({
        submitIncidentReportRequest: payload
      })
      return created.id ?? null
    } catch (error) {
      console.error('Store: Failed to submit report', error)
      return null
    } finally {
      // Always release the loading state whether it succeeds or fails
      isSubmitting.value = false
    }
  }

  return {
    $reset,
    step,
    maxStepIndex,
    isSubmitting,
    nearbyCrashes,
    selectedCrashId,
    isLoadingCrashes,
    relatedTo,
    loadNearbyCrashes,
    continueFromRefine,
    selectCrash,
    prefillFromCrash,
    activeStepOrder,
    currentStepIndex,
    isLastStep,
    nextStep,
    previousStep,
    jumpToStep,
    isStepDone,
    incidentForm,
    updateGeoJsonLocation,
    prefillFromBounty,
    prefillFromRelatedReport,
    clearRelated,
    applyDashcamMetadata,
    submitReport
  }
})
